import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './CustomDropdown.css';

// Hook to detect if we're on mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 500);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);
  
  return isMobile;
};

interface DropdownOption {
  id: string;
  name: string;
  disabled?: boolean;
  isCategoryHeader?: boolean;
  brand?: string;
}

interface CustomDropdownProps {
  id: string;
  name: string;
  value: string;
  options: DropdownOption[];
  placeholder?: string;
  onChange: (value: string) => void;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  id,
  name,
  value,
  options,
  placeholder = "Select a phone",
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  // Close dropdown when clicking outside (desktop only)
  useEffect(() => {
    if (isMobile) return; // Don't apply to mobile

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile]);

  const handleSelect = (optionId: string) => {
    onChange(optionId);
    setIsOpen(false);
    setSearchText(''); // Clear search when selection is made
  };

  // Filter options based on search text (pure functional style)
  const getFilteredOptions = (): DropdownOption[] => {
    if (!searchText.trim()) {
      return options;
    }

    const searchLower = searchText.toLowerCase();
    const matchesSearch = (option: DropdownOption) => 
      option.name.toLowerCase().includes(searchLower);

    // Split into categories using pure functions
    const result = options.reduce<{
      categories: Array<{header: DropdownOption, items: DropdownOption[]}>,
      currentCategory: {header: DropdownOption, items: DropdownOption[]} | null
    }>(
      (acc, option) => {
        if (option.isCategoryHeader) {
          const newCategory = { header: option, items: [] };
          return {
            categories: acc.currentCategory 
              ? [...acc.categories, acc.currentCategory] 
              : acc.categories,
            currentCategory: newCategory
          };
        } else {
          return acc.currentCategory 
            ? {
                ...acc,
                currentCategory: {
                  ...acc.currentCategory,
                  items: [...acc.currentCategory.items, option]
                }
              }
            : acc;
        }
      },
      { categories: [], currentCategory: null }
    );

    // Add the final category if it exists
    const allCategories = result.currentCategory 
      ? [...result.categories, result.currentCategory]
      : result.categories;

    // Filter and flatten - purely functional
    return allCategories
      .filter(category => category.items.some(matchesSearch))
      .flatMap(category => [
        category.header,
        ...category.items.filter(matchesSearch)
      ]);
  };

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && !isMobile && searchInputRef.current) {
      // Small delay to ensure the dropdown is rendered
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [isOpen, isMobile]);

  // Clear search when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchText('');
    }
  }, [isOpen]);

  // Handle mobile modal interactions
  useEffect(() => {
    if (!isMobile || !isOpen) return;

    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    
    let scrollTimeout: NodeJS.Timeout;
    
    const handleScroll = (e: Event) => {
      // Don't close if the scroll is happening within the modal
      const target = e.target as Element;
      if (target && target.closest('.mobile-dropdown-modal')) {
        return;
      }
      
      // Debounce scroll events to avoid closing during keyboard appearance
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        // Only close if we're not currently focused on an input
        const activeElement = document.activeElement;
        if (!activeElement || !activeElement.closest('.mobile-dropdown-modal')) {
          setIsOpen(false);
        }
      }, 150);
    };

    document.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      document.body.style.overflow = '';
      clearTimeout(scrollTimeout);
      document.removeEventListener('scroll', handleScroll);
    };
  }, [isOpen, isMobile]);

  const selectedOption = options.find(opt => opt.id === value);

  // Mobile full-screen modal implementation
  if (isMobile) {
    return (
      <>
        <div className="custom-dropdown mobile-custom" ref={dropdownRef}>
          <button
            type="button"
            id={id}
            name={name}
            className="dropdown-trigger mobile-trigger"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
          >
            <span
              className={`dropdown-value ${!value ? 'placeholder' : ''}`}
              title={selectedOption?.name || placeholder}
            >
              {selectedOption?.name || placeholder}
            </span>
            <svg
              className="dropdown-arrow"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M7 10L12 15L17 10"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Mobile full-screen modal - render as portal */}
        {isOpen && createPortal(
          <div 
            className="mobile-dropdown-overlay"
            onClick={(e) => {
              // Close if clicking on overlay background
              if (e.target === e.currentTarget) {
                setIsOpen(false);
              }
            }}
          >
            <div className="mobile-dropdown-modal">
              {/* Header with close button */}
              <div className="mobile-dropdown-header">
                <button
                  type="button"
                  className="mobile-close-button"
                  onClick={() => setIsOpen(false)}
                  aria-label="Close dropdown"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 15L12 9L6 15" />
                  </svg>
                </button>
                <span className="mobile-dropdown-title">Select Product</span>
              </div>

              {/* Search input */}
              <div className="mobile-search-container">
                <input
                  ref={searchInputRef}
                  type="text"
                  className="mobile-search-input"
                  placeholder="Type to filter..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onFocus={(e) => {
                    // Prevent any scroll-based closing when input is focused
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    // Prevent overlay click handler from closing modal
                    e.stopPropagation();
                  }}
                />
                {searchText && (
                  <button
                    type="button"
                    className="mobile-search-clear"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSearchText('');
                    }}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>

              {/* Options list */}
              <div className="mobile-options-container">
                {getFilteredOptions().map((option) => (
                  option.isCategoryHeader ? (
                    <div
                      key={option.id}
                      className="mobile-category-header"
                      role="presentation"
                    >
                      <span className="category-header-text">{option.name}</span>
                    </div>
                  ) : (
                    <button
                      key={option.id}
                      type="button"
                      className={`mobile-option ${option.disabled ? 'disabled' : ''} ${value === option.id ? 'selected' : ''}`}
                      onClick={() => !option.disabled && handleSelect(option.id)}
                      disabled={option.disabled}
                      role="option"
                      aria-selected={value === option.id}
                      aria-disabled={option.disabled}
                    >
                      <span className="mobile-option-text" title={option.name}>{option.name}</span>
                      {value === option.id && (
                        <svg className="mobile-checkmark" width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path
                            d="M16.25 6.25L8.125 14.375L3.75 10"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </button>
                  )
                ))}
                
                {/* No results message */}
                {getFilteredOptions().length === 0 && searchText.trim() && (
                  <div className="mobile-no-results">
                    No products found matching "{searchText}"
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
      </>
    );
  }

  // Desktop custom dropdown implementation
  return (
    <div className="custom-dropdown" ref={dropdownRef}>
      <button
        type="button"
        className={`dropdown-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span
          className={`dropdown-value ${!value ? 'placeholder' : ''}`}
          title={selectedOption?.name || placeholder}
        >
          {selectedOption?.name || placeholder}
        </span>
        <svg
          className="dropdown-arrow"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M7 10L12 15L17 10"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="dropdown-menu" role="listbox">
          {/* Search input for filtering */}
          <div className="dropdown-search-container">
            <input
              ref={searchInputRef}
              type="text"
              className="dropdown-search-input"
              placeholder="Type to filter..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onClick={(e) => e.stopPropagation()} // Prevent dropdown from closing
            />
            {searchText && (
              <button
                type="button"
                className="dropdown-search-clear"
                onClick={() => setSearchText('')}
                title="Clear search"
              >
                ×
              </button>
            )}
          </div>
          
          {/* Filtered options */}
          <div className="dropdown-options-container">
            {getFilteredOptions().map((option) => (
              option.isCategoryHeader ? (
                <div
                  key={option.id}
                  className="dropdown-category-header"
                  role="presentation"
                >
                  <span className="category-header-text">{option.name}</span>
                </div>
              ) : (
                <div
                  key={option.id}
                  className={`dropdown-option ${option.disabled ? 'disabled' : ''} ${value === option.id ? 'selected' : ''}`}
                  onClick={() => !option.disabled && handleSelect(option.id)}
                  role="option"
                  aria-selected={value === option.id}
                  aria-disabled={option.disabled}
                >
                  <span className="option-text" title={option.name}>{option.name}</span>
                  <input
                    type="radio"
                    className="option-radio"
                    checked={value === option.id}
                    onChange={() => { }}
                    disabled={option.disabled}
                    tabIndex={-1}
                  />
                </div>
              )
            ))}
            
            {/* No results message */}
            {getFilteredOptions().length === 0 && searchText.trim() && (
              <div className="dropdown-no-results">
                No products found matching "{searchText}"
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDropdown;
